import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["counter", "dropdown", "list", "markAllButton"]
  static values = { 
    userId: Number,
    refreshInterval: { type: Number, default: 30000 } // 30 secondes par défaut
  }

  connect() {
    this.isOpen = false
    this.refreshTimer = null
    this.startPeriodicRefresh()
    
    // Fermer le dropdown si on clique ailleurs
    document.addEventListener('click', this.closeOnOutsideClick.bind(this))
  }

  disconnect() {
    this.stopPeriodicRefresh()
    document.removeEventListener('click', this.closeOnOutsideClick.bind(this))
  }

  // Ouvrir/fermer le dropdown
  toggle() {
    this.isOpen = !this.isOpen
    
    if (this.isOpen) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.isOpen = true
    this.dropdownTarget.classList.remove('hidden', 'opacity-0', 'scale-95')
    this.dropdownTarget.classList.add('opacity-100', 'scale-100')
    
    // Charger les notifications récentes quand on ouvre
    this.loadRecentNotifications()
  }

  close() {
    this.isOpen = false
    this.dropdownTarget.classList.remove('opacity-100', 'scale-100')
    this.dropdownTarget.classList.add('opacity-0', 'scale-95')
    
    setTimeout(() => {
      if (!this.isOpen) {
        this.dropdownTarget.classList.add('hidden')
      }
    }, 150)
  }

  closeOnOutsideClick(event) {
    if (this.isOpen && !this.element.contains(event.target)) {
      this.close()
    }
  }

  // Actualiser le compteur de notifications non lues
  async refreshUnreadCount() {
    try {
      const response = await fetch('/admin/notifications/unread_count', {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        this.updateCounter(data.unread_count)
      }
    } catch (error) {
      console.log('Erreur lors de la récupération du compteur:', error)
    }
  }

  // Charger les notifications récentes pour le dropdown
  async loadRecentNotifications() {
    try {
      const response = await fetch('/admin/notifications/recent', {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        this.updateDropdownContent(data.notifications)
        this.updateCounter(data.unread_count)
      }
    } catch (error) {
      console.log('Erreur lors du chargement des notifications:', error)
    }
  }

  // Mettre à jour le compteur
  updateCounter(count) {
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = count
      
      if (count > 0) {
        this.counterTarget.classList.remove('hidden')
        this.counterTarget.classList.add('animate-pulse')
      } else {
        this.counterTarget.classList.add('hidden')
        this.counterTarget.classList.remove('animate-pulse')
      }
    }
  }

  // Mettre à jour le contenu du dropdown
  updateDropdownContent(notifications) {
    if (!this.hasListTarget) return

    if (notifications.length === 0) {
      this.listTarget.innerHTML = `
        <div class="p-4 text-center text-gray-500">
          <div class="text-4xl mb-2">🔔</div>
          <p class="text-sm">Aucune notification</p>
        </div>
      `
      return
    }

    const notificationsHtml = notifications.map(notification => `
      <div class="p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
        <div class="flex items-start space-x-3">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <span class="text-white text-sm">${notification.icon}</span>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-900 truncate">${notification.title}</p>
            <p class="text-xs text-gray-500 truncate">${notification.message}</p>
            <div class="flex items-center justify-between mt-1">
              <span class="text-xs text-gray-400">${notification.time_ago}</span>
              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                ${notification.priority === 'high' ? 'bg-red-100 text-red-800' : 
                  notification.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' : 
                  'bg-green-100 text-green-800'}">
                ${notification.priority}
              </span>
            </div>
          </div>
        </div>
      </div>
    `).join('')

    this.listTarget.innerHTML = notificationsHtml
  }

  // Marquer une notification comme lue
  async markAsRead(event) {
    const notificationId = event.target.dataset.notificationId
    if (!notificationId) return

    try {
      const response = await fetch(`/admin/notifications/${notificationId}/mark_as_read`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      })
      
      if (response.ok) {
        // Actualiser les notifications
        this.loadRecentNotifications()
      }
    } catch (error) {
      console.log('Erreur lors du marquage comme lu:', error)
    }
  }

  // Marquer toutes les notifications comme lues
  async markAllAsRead() {
    try {
      const response = await fetch('/admin/notifications/mark_all_as_read', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      })
      
      if (response.ok) {
        // Actualiser les notifications
        this.loadRecentNotifications()
        
        // Afficher un message de succès (optionnel)
        this.showSuccessMessage('Toutes les notifications ont été marquées comme lues')
      }
    } catch (error) {
      console.log('Erreur lors du marquage global:', error)
    }
  }

  // Gestion de la périodicité
  startPeriodicRefresh() {
    this.stopPeriodicRefresh()
    this.refreshTimer = setInterval(() => {
      this.refreshUnreadCount()
    }, this.refreshIntervalValue)
  }

  stopPeriodicRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  // Afficher un message de succès (optionnel)
  showSuccessMessage(message) {
    // Créer un flash message temporaire
    const flashDiv = document.createElement('div')
    flashDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 transform transition-all duration-300'
    flashDiv.textContent = message
    
    document.body.appendChild(flashDiv)
    
    // Animation d'entrée
    setTimeout(() => {
      flashDiv.classList.add('translate-y-0')
    }, 10)
    
    // Suppression après 3 secondes
    setTimeout(() => {
      flashDiv.classList.add('translate-y-full', 'opacity-0')
      setTimeout(() => {
        document.body.removeChild(flashDiv)
      }, 300)
    }, 3000)
  }

  // Méthodes pour les actions depuis les vues
  viewAll() {
    window.location.href = '/admin/notifications'
  }

  settings() {
    // TODO: Implémenter les paramètres de notifications
    console.log('Paramètres de notifications')
  }
}